// src/pages/reports/DailyOrdersVsReturnsSummary.tsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  X,
  ChevronDown,
  Calendar,
  Download,
  Filter,
  FileText,
  FileSpreadsheet,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI, branchesAPI, returnsAPI, departmentAPI } from '../services/api';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  format,
  isWithinInterval,
  parseISO,
  isSameDay,
} from 'date-fns';
import { arSA } from 'date-fns/locale';

// ==============================
// === INTERFACES ===
// ==============================
interface OrderRow {
  id: string;
  product: string;
  unit: string;
  department: string;
  departmentId: string;
  branchQuantities: { [branch: string]: { orders: number; returns: number } };
  totalOrders: number;
  totalReturns: number;
}
interface Branch {
  _id: string;
  name: string;
  nameEn?: string;
  displayName: string;
}
interface Department {
  _id: string;
  name: string;
  nameEn?: string;
}

// ==============================
// === CONSTANTS ===
// ==============================
const PERIOD_OPTIONS = [
  { value: 'today', labelAr: 'اليوم', labelEn: 'Today' },
  { value: 'week', labelAr: 'آخر 7 أيام', labelEn: 'Last 7 Days' },
  { value: 'month', labelAr: 'آخر 30 يوم', labelEn: 'Last 30 Days' },
  { value: 'custom', labelAr: 'فترة مخصصة', labelEn: 'Custom Range' },
];

// ==============================
// === UTILS ===
// ==============================
const safeString = (value: any, fallback = '') => (typeof value === 'string' ? value.trim() : fallback);
const safeNumber = (value: any, fallback = 0) => (typeof value === 'number' && !isNaN(value) ? value : fallback);
const toArabicNumerals = (number: string | number): string => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return String(number).replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
};
const formatNumberWithComma = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
const formatNumber = (num: number, isRtl: boolean): string => {
  const formatted = formatNumberWithComma(num);
  return isRtl ? toArabicNumerals(formatted) : formatted;
};

// ==============================
// === SUB-COMPONENTS ===
// ==============================
const ProductSearchInput = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  ariaLabel: string;
}) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${isRtl ? 'pl-10 pr-3' : 'pr-10 pl-3'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white text-[12px] ${isRtl ? 'text-right' : 'text-left'}`}
        aria-label={ariaLabel}
      />
      {value ? (
        <button
          onClick={() => onChange({ target: { value: '' } } as any)}
          className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-amber-600`}
          aria-label={isRtl ? 'مسح' : 'Clear'}
        >
          <X className="w-4 h-4" />
        </button>
      ) : (
        <Search className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
      )}
    </div>
  );
};

const Button: React.FC<{
  variant: 'primary' | 'outline';
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ variant, onClick, disabled, children, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
      variant === 'primary'
        ? 'bg-amber-600 hover:bg-amber-700 text-white shadow'
        : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
    } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
  >
    {icon && icon}
    {children}
  </button>
);

const MultiSelectDropdown: React.FC<{
  value: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
}> = ({ value, onChange, options, ariaLabel }) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleOption = (optionValue: string) => {
    if (optionValue === 'all') {
      onChange(value.includes('all') ? [] : ['all']);
    } else {
      if (value.includes('all')) {
        onChange([optionValue]);
      } else {
        if (value.includes(optionValue)) {
          const newValues = value.filter(v => v !== optionValue);
          onChange(newValues.length === 0 ? ['all'] : newValues);
        } else {
          onChange([...value.filter(v => v !== 'all'), optionValue]);
        }
      }
    }
  };

  const allSelected = value.includes('all') || value.length === options.length - 1;
  const count = value.filter(v => v !== 'all').length;
  const displayLabel = allSelected
    ? isRtl ? 'جميع الأقسام' : 'All Departments'
    : isRtl
      ? `${count} ${count === 1 ? 'قسم' : 'أقسام'}`
      : `${count} ${count === 1 ? 'Department' : 'Departments'}`;

  const displayOptions = [...options.filter(opt => opt.value !== 'all'), options.find(opt => opt.value === 'all')!];

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        dir={isRtl ? 'rtl' : 'ltr'}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white shadow-sm text-xs font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'}`}
        aria-label={ariaLabel}
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
        >
          {displayOptions.map(opt => (
            <div key={opt.value} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
              <span className="text-xs text-gray-700">{opt.label}</span>
              <input
                type="checkbox"
                checked={value.includes(opt.value) || (opt.value === 'all' && allSelected)}
                onChange={() => toggleOption(opt.value)}
                className="h-4 w-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
              />
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

const PeriodDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
}> = ({ value, onChange, options, ariaLabel }) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || options[0];
  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        dir={isRtl ? 'rtl' : 'ltr'}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white shadow-sm text-xs font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'}`}
        aria-label={ariaLabel}
      >
        <span className="truncate">{selectedOption?.label || ''}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
        >
          {options.map(opt => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-xs ${isRtl ? 'text-right' : 'text-left'} text-gray-700 hover:bg-amber-50 hover:text-amber-700`}
            >
              {opt.label}
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
};

// ==============================
// === EXCEL EXPORT ===
// ==============================
const exportToExcel = async (
   data: any[][],
  headers: string[],
  periodLabel: string,
  departmentsLabel: string,
  fileNamePrefix: string,
  isRtl: boolean
) => {
  const toastId = 'excel-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف Excel...' : 'Generating Excel...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(isRtl ? 'مقارنة_الطلبات_المرتجعات' : 'Orders_vs_Returns');

    const amber50 = { argb: 'FFF9F2E5' };
    const amber700 = { argb: 'FFB45309' };
    const green100 = { argb: 'FFDCFCE7' };
    const red100 = { argb: 'FFFCECEC' };
    const gray100 = { argb: 'FFF9FAFB' };
    const borderColor = { argb: 'FFE5E7EB' };
    const gray500 = { argb: 'FF6B7280' };
    const gray700 = { argb: 'FF374151' };

    // العنوان
    worksheet.mergeCells(1, 1, 1, headers.length);
    const titleCell = worksheet.getCell('A1');
    titleCell.value = isRtl ? 'مقارنة الطلبات والمرتجعات' : 'Orders vs Returns Summary';
    titleCell.font = { size: 16, bold: true, color: amber700, name: 'Amiri' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: amber50 };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.border = {
      top: { style: 'thin', color: borderColor },
      left: { style: 'thin', color: borderColor },
      bottom: { style: 'thin', color: borderColor },
      right: { style: 'thin', color: borderColor },
    };

    // الفترة
    worksheet.mergeCells(2, 1, 2, headers.length);
    const periodCell = worksheet.getCell('A2');
    periodCell.value = periodLabel;
    periodCell.font = { size: 11, color: gray500, name: 'Amiri' };
    periodCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // الأقسام
    let headerRow = 3;
    if (departmentsLabel) {
      worksheet.mergeCells(3, 1, 3, headers.length);
      const deptCell = worksheet.getCell('A3');
      deptCell.value = departmentsLabel;
      deptCell.font = { size: 10, color: gray500, name: 'Amiri' };
      deptCell.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow = 4;
    }

    if (isRtl) worksheet.views = [{ rightToLeft: true }];

    const processedData = data.map(row =>
      row.map(cell => (typeof cell === 'number' ? formatNumber(cell, isRtl) : cell))
    );

    const headerRowCells = worksheet.addRow(headers);
    headerRowCells.eachCell((cell) => {
      cell.font = { bold: true, size: 11, color: amber700, name: 'Amiri' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: amber50 };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: borderColor },
        left: { style: 'thin', color: borderColor },
        bottom: { style: 'thin', color: borderColor },
        right: { style: 'thin', color: borderColor },
      };
    });

    processedData.forEach((row, idx) => {
      const isTotalRow = idx === processedData.length - 1;
      const excelRow = worksheet.addRow(row);
      excelRow.eachCell((cell, colIndex) => {
        let fillColor = { argb: 'FFFFFFFF' };
        if (isTotalRow) {
          fillColor = amber50;
        } else {
          const branchCount = (headers.length - 2) / 2;
          const isOrdersCol = colIndex > 1 && colIndex <= 1 + 2 * branchCount && (colIndex - 2) % 2 === 0;
          const isReturnsCol = colIndex > 1 && colIndex <= 1 + 2 * branchCount && (colIndex - 2) % 2 === 1;

          if (typeof row[colIndex - 1] === 'string') {
            const latinNum = row[colIndex - 1].replace(/[٠-٩]/g, d => '0123456789'['٠١٢٣٤٥٦٧٨٩'.indexOf(d)]);
            const numValue = Number(latinNum);
            if (isOrdersCol && numValue > 0) {
              fillColor = green100;
            } else if (isReturnsCol && numValue > 0) {
              fillColor = red100;
            } else if (idx % 2 === 0) {
              fillColor = gray100;
            }
          } else if (idx % 2 === 0) {
            fillColor = gray100;
          }
        }

        cell.font = { size: 10, bold: isTotalRow, color: isTotalRow ? amber700 : gray700, name: 'Amiri' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: fillColor };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
          top: { style: 'thin', color: borderColor },
          left: { style: 'thin', color: borderColor },
          bottom: { style: 'thin', color: borderColor },
          right: { style: 'thin', color: borderColor },
        };
      });
    });

    worksheet.columns.forEach(column => {
      let maxLength = 12;
      if (column.eachCell) {
        column.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.value) {
            const length = cell.value.toString().length;
            if (length > maxLength) maxLength = length;
          }
        });
      }
      column.width = Math.min(30, Math.max(12, maxLength + 2));
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `${fileNamePrefix}_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى Excel بنجاح!' : 'Exported to Excel successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Excel Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير Excel. حاول مرة أخرى.' : 'Excel export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === PDF EXPORT ===
// ==============================
const loadAmiriFont = async (doc: jsPDF): Promise<boolean> => {
  const fontName = 'Amiri';
  try {
    const [regularRes, boldRes] = await Promise.all([
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@master/fonts/Amiri-Regular.ttf'),
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@master/fonts/Amiri-Bold.ttf'),
    ]);
    const [regularFontBytes, boldFontBytes] = await Promise.all([
      regularRes.arrayBuffer(),
      boldRes.arrayBuffer(),
    ]);
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return window.btoa(binary);
    };
    doc.addFileToVFS(`${fontName}-normal.ttf`, arrayBufferToBase64(regularFontBytes));
    doc.addFont(`${fontName}-normal.ttf`, fontName, 'normal');
    doc.addFileToVFS(`${fontName}-bold.ttf`, arrayBufferToBase64(boldFontBytes));
    doc.addFont(`${fontName}-bold.ttf`, fontName, 'bold');
    return true;
  } catch (error) {
    console.warn('Failed to load Amiri font. Using fallback.');
    return false;
  }
};

const wrapTextNoSplit = (text: string, maxWidth: number, doc: jsPDF, font: string, fontSize: number): string[] => {
  doc.setFont(font, 'normal');
  doc.setFontSize(fontSize);
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const width = doc.getTextWidth(testLine);
    if (width > maxWidth) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        lines.push(word);
        currentLine = '';
      }
    } else {
      currentLine = testLine;
    }
    if (lines.length >= 2) break;
  }
  if (currentLine) lines.push(currentLine);
  return lines;
};

const exportToPDF = async (
 data:  any[][],
  periodLabel: string,
  departmentsLabel: string,
  headers: string[],
  isRtl: boolean,
  fileNamePrefix: string
) => {
  const toastId = 'pdf-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف PDF...' : 'Generating PDF...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const fontLoaded = await loadAmiriFont(doc);
    const fontName = fontLoaded ? 'Amiri' : 'helvetica';
    const pageWidth = doc.internal.pageSize.width;

    doc.setFont(fontName, 'bold');
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    const title = isRtl ? 'مقارنة الطلبات والمرتجعات' : 'Orders vs Returns Summary';
    doc.text(title, pageWidth / 2, 20, { align: 'center' });

    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    doc.text(periodLabel, pageWidth / 2, 28, { align: 'center' });

    if (departmentsLabel) {
      doc.setFontSize(10);
      const deptLines = wrapTextNoSplit(departmentsLabel, pageWidth - 40, doc, fontName, 10);
      doc.text(deptLines, pageWidth / 2, 35, { align: 'center' });
    }

    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.line(15, 43, pageWidth - 15, 43);

    const processedData = isRtl ? data.map(row => row.slice().reverse()) : data;
    const processedHeaders = isRtl ? headers.slice().reverse() : headers;
    const totalColumns = processedHeaders.length;

    const contentWidth = pageWidth - 30;
    const firstColMin = 40;
    const lastColsMin = 40;
    const branchMin = 20;
    const availableWidth = contentWidth - firstColMin - lastColsMin;
    const branchColWidth = Math.max(branchMin, availableWidth / Math.max(1, totalColumns - 3));
    const firstColWidth = Math.min(70, firstColMin + Math.max(0, (availableWidth - (totalColumns - 3) * branchColWidth) / 2));
    const lastColsWidth = lastColsMin;

    const columnStyles: Record<number, any> = { 0: { cellWidth: firstColWidth } };
    for (let i = 1; i < totalColumns - 2; i++) {
      columnStyles[i] = { cellWidth: branchColWidth };
    }
    columnStyles[totalColumns - 2] = { cellWidth: lastColsWidth / 2 };
    columnStyles[totalColumns - 1] = { cellWidth: lastColsWidth / 2 };

    const tableConfig = {
      head: [processedHeaders],
      body: processedData,
      startY: 49,
      theme: 'grid',
      tableWidth: contentWidth,
      headStyles: {
        fillColor: [250, 240, 220],
        textColor: [70, 60, 40],
        fontSize: 8,
        halign: 'center',
        valign: 'middle',
        overflow: 'linebreak',
        font: fontName,
        fontStyle: 'bold',
        cellPadding: 2,
      },
      bodyStyles: {
        fontSize: 8,
        halign: 'center',
        valign: 'middle',
        font: fontName,
        textColor: [50, 50, 50],
        cellPadding: 2,
        minCellHeight: 10,
      },
      alternateRowStyles: { fillColor: [254, 253, 250] },
      columnStyles,
      willDrawCell: (hookData: any) => {
        if ((hookData.section === 'body' && hookData.column.index === 0) || hookData.section === 'head') {
          const text = hookData.cell.text[0] || '';
          hookData.cell.text = wrapTextNoSplit(text, hookData.cell.width - 4, doc, fontName, 8);
        }
      },
      didParseCell: (hookData: any) => {
        hookData.cell.text = hookData.cell.text.map((text: string) =>
          /[0-9]/.test(text) ? toArabicNumerals(text) : text
        );
        if (hookData.section === 'body' && hookData.row.index === hookData.table.body.length - 1) {
          hookData.cell.styles.fontStyle = 'bold';
          hookData.cell.styles.fillColor = [245, 235, 215];
        }
        if (hookData.section === 'body') {
          const colIndex = hookData.column.index;
          const totalCols = processedHeaders.length;
          const branchCount = (totalCols - 3) / 2;
          const isOrdersCol = colIndex >= 1 && colIndex <= 1 + 2 * branchCount && (colIndex - 1) % 2 === 0;
          const isReturnsCol = colIndex >= 1 && colIndex <= 1 + 2 * branchCount && (colIndex - 1) % 2 === 1;

          if (isOrdersCol && Number(hookData.cell.raw) > 0) {
            hookData.cell.styles.fillColor = [220, 255, 220];
          } else if (isReturnsCol && Number(hookData.cell.raw) > 0) {
            hookData.cell.styles.fillColor = [255, 220, 220];
          }
        }
      },
    };

    autoTable(doc, tableConfig as any);

    const dateStr = new Date().toLocaleDateString(isRtl ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const footer = isRtl
      ? `نظام إدارة الجودياء – ${toArabicNumerals(dateStr)}`
      : `Elgoodia Management System – ${dateStr}`;
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(footer, pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });

    const fileName = `${fileNamePrefix}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير بنجاح!' : 'Exported successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('PDF Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل التصدير. حاول مرة أخرى.' : 'Export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === MAIN COMPONENT ===
// ==============================
const DailyOrdersVsReturnsSummary: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRtl = language === 'ar';
  const [ordersData, setOrdersData] = useState<OrderRow[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>(['all']);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [period, setPeriod] = useState('today');

  const periodOptions = useMemo(
    () => PERIOD_OPTIONS.map(p => ({
      value: p.value,
      label: isRtl ? p.labelAr : p.labelEn
    })),
    [isRtl]
  );

  const fetchData = useCallback(async () => {
    if (!user || !['admin', 'production'].includes(user.role)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [deptsRes, branchesRes] = await Promise.all([
        departmentAPI.getAll({ limit: 100 }),
        branchesAPI.getAll()
      ]);

      const deptsData = Array.isArray(deptsRes) ? deptsRes : (deptsRes.data || []);
      setDepartments(deptsData);

      const branchesData = Array.isArray(branchesRes)
        ? branchesRes.map(b => ({
            _id: b._id,
            name: b.name || (isRtl ? 'غير معروف' : 'Unknown'),
            nameEn: b.nameEn,
            displayName: isRtl ? b.name : (b.nameEn || b.name),
          }))
        : [];
      setBranches(branchesData);

      const [ordersRes, returnsRes] = await Promise.all([
        ordersAPI.getAll({ status: { $ne: 'cancelled' }, limit: 10000 }),
        returnsAPI.getAll({ limit: 10000 })
      ]);

      const orders = Array.isArray(ordersRes) ? ordersRes : (ordersRes.orders || []);
      const returns = Array.isArray(returnsRes) ? (returnsRes.returns || returnsRes) : [];

      const now = new Date();
      let start: Date, end: Date;
      switch (period) {
        case 'today':
          start = startOfDay(now);
          end = endOfDay(now);
          break;
        case 'week':
          start = subDays(now, 6);
          end = endOfDay(now);
          break;
        case 'month':
          start = subMonths(now, 1);
          end = endOfDay(now);
          break;
        case 'custom':
          start = startDate ? startOfDay(parseISO(startDate)) : startOfDay(now);
          end = endDate ? endOfDay(parseISO(endDate)) : endOfDay(now);
          break;
        default:
          start = startOfDay(now);
          end = endOfDay(now);
      }

      const validOrders = orders.filter(o =>
        o.createdAt && isWithinInterval(parseISO(o.createdAt), { start, end })
      );
      const validReturns = returns.filter(r =>
        r.createdAt && isWithinInterval(parseISO(r.createdAt), { start, end }) && r.status === 'approved'
      );

      const branchMap = new Map(branchesData.map(b => [b._id, b.displayName]));
      const allBranchNames = branchesData.map(b => b.displayName).sort((a, b) => a.localeCompare(b, isRtl ? 'ar' : 'en'));

      const rowMap = new Map<string, OrderRow>();

      // Process Orders
      validOrders.forEach(order => {
        const branchName = branchMap.get(order.branch?._id) || (isRtl ? 'الفرع الرئيسي' : 'Main Branch');
        (order.items || []).forEach((item: any) => {
          if (item.rejected || item.rejectedByBranch) return;
          const pid = item.product?._id;
          if (!pid) return;
          const pName = isRtl ? (item.product?.name || 'غير معروف') : (item.product?.nameEn || item.product?.name || 'Deleted');
          const unit = isRtl ? (item.product?.unit || 'وحدة') : (item.product?.unitEn || 'Unit');
          const dept = item.product?.department;
          const deptId = dept?._id || 'general';
          const deptName = isRtl ? (dept?.name || 'عام') : (dept?.nameEn || dept?.name || 'General');

          if (!rowMap.has(pid)) {
            const initBranchQty: { [k: string]: { orders: number; returns: number } } = {};
            allBranchNames.forEach(b => initBranchQty[b] = { orders: 0, returns: 0 });
            rowMap.set(pid, {
              id: pid,
              product: pName,
              unit,
              department: deptName,
              departmentId: deptId,
              branchQuantities: initBranchQty,
              totalOrders: 0,
              totalReturns: 0,
            });
          }
          const row = rowMap.get(pid)!;
          row.branchQuantities[branchName].orders += Number(item.quantity) || 0;
          row.totalOrders += Number(item.quantity) || 0;
        });
      });

      // Process Returns
      validReturns.forEach(ret => {
        const branchName = branchMap.get(ret.branch?._id) || (isRtl ? 'الفرع الرئيسي' : 'Main Branch');
        (ret.items || []).forEach((item: any) => {
          const pid = item.product?._id;
          if (!pid) return;
          if (!rowMap.has(pid)) {
            const details = { name: 'غير معروف', unit: 'وحدة', deptId: 'general', deptName: 'عام' };
            const initBranchQty: { [k: string]: { orders: number; returns: number } } = {};
            allBranchNames.forEach(b => initBranchQty[b] = { orders: 0, returns: 0 });
            rowMap.set(pid, {
              id: pid,
              product: details.name,
              unit: details.unit,
              department: details.deptName,
              departmentId: details.deptId,
              branchQuantities: initBranchQty,
              totalOrders: 0,
              totalReturns: 0,
            });
          }
          const row = rowMap.get(pid)!;
          row.branchQuantities[branchName].returns += Number(item.quantity) || 0;
          row.totalReturns += Number(item.quantity) || 0;
        });
      });

      setOrdersData(Array.from(rowMap.values()).sort((a, b) => (b.totalOrders + b.totalReturns) - (a.totalOrders + a.totalReturns)));
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error(isRtl ? 'فشل جلب البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user, period, startDate, endDate, isRtl]);

  useEffect(() => {
    setPeriod('today');
  }, []);

  useEffect(() => {
    if (user && ['admin', 'production'].includes(user.role)) {
      fetchData();
    }
  }, [period, startDate, endDate, user, isRtl, fetchData]);

  const departmentOptions = useMemo(() => {
    const baseOptions = departments.map(d => ({
      value: d._id,
      label: isRtl ? d.name : (d.nameEn || d.name),
    })).sort((a, b) => a.label.localeCompare(b.label, isRtl ? 'ar' : 'en'));
    return [...baseOptions, { value: 'all', label: isRtl ? 'جميع الأقسام' : 'All Departments' }];
  }, [departments, isRtl]);

  const filteredData = useMemo(() => {
    const allSelected = selectedDepartments.includes('all');
    return ordersData.filter(row => {
      const matchesSearch = row.product.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment = allSelected || selectedDepartments.includes(row.departmentId);
      return matchesSearch && matchesDepartment;
    });
  }, [ordersData, searchTerm, selectedDepartments]);

  const allBranches = useMemo(() => 
    branches.map(b => b.displayName).sort((a, b) => a.localeCompare(b, isRtl ? 'ar' : 'en')), 
    [branches, isRtl]
  );

  const periodLabel = useMemo(() => {
    const now = new Date();
    if (period === 'custom') {
      if (startDate && endDate && isSameDay(parseISO(startDate), parseISO(endDate))) {
        return isRtl
          ? `يوم واحد - ${format(parseISO(startDate), 'EEEE d/M/yyyy', { locale: arSA })}`
          : `Single Day - ${format(parseISO(startDate), 'EEEE d/M/yyyy')}`;
      }
      return startDate && endDate
        ? `${format(parseISO(startDate), 'd/M/yyyy')} - ${format(parseISO(endDate), 'd/M/yyyy')}`
        : isRtl ? 'الفترة المخصصة' : 'Custom Range';
    }
    if (period === 'today') {
      return isRtl
        ? `اليوم - ${format(now, 'EEEE d/M/yyyy', { locale: arSA })}`
        : `Today - ${format(now, 'EEEE d/M/yyyy')}`;
    }
    return periodOptions.find(p => p.value === period)?.label || '';
  }, [period, startDate, endDate, isRtl, periodOptions]);

  const departmentsLabel = useMemo(() => {
    if (selectedDepartments.includes('all')) return '';
    const selectedNames = selectedDepartments
      .filter(v => v !== 'all')
      .map(id => departments.find(d => d._id === id)?.name || '')
      .filter(Boolean);
    return isRtl
      ? `الأقسام: ${selectedNames.join(' - ')}`
      : `Departments: ${selectedNames.join(' - ')}`;
  }, [selectedDepartments, departments, isRtl]);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    setPeriod('custom');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    setPeriod('custom');
  };

  const grandTotalOrders = useMemo(() =>
    filteredData.reduce((s, r) => s + r.totalOrders, 0), [filteredData]);
  const grandTotalReturns = useMemo(() =>
    filteredData.reduce((s, r) => s + r.totalReturns, 0), [filteredData]);

  if (!user || !['admin', 'production'].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <span className="text-xs text-red-600">{isRtl ? 'غير مصرح لك' : 'Not authorized'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 mx-auto">
      <div className="w-full mb-4">
        <div className="flex justify-between items-center gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-xl">
              <Calendar className="w-5 h-5 text-amber-700" />
            </div>
            <h1 className="text-xl font-bold text-gray-800">
              {isRtl ? 'مقارنة الطلبات والمرتجعات' : 'Orders vs Returns Summary'}
            </h1>
          </div>
          <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <div className="relative group">
              <Button
                variant="primary"
                disabled={filteredData.length === 0}
                icon={<Download className="w-4 h-4" />}
              >
                {isRtl ? 'تصدير' : 'Export'}
              </Button>
              <div className={`absolute hidden group-hover:block mt-2 w-48 rounded-lg shadow-lg bg-white z-50 border border-gray-200 overflow-hidden ${isRtl ? 'left-0' : 'right-0'}`}>
                <button
                  onClick={async () => {
                    const headers = [
                      isRtl ? 'اسم المنتج' : 'Product Name',
                      ...allBranches.flatMap(b => [
                        b + (isRtl ? ' (طلبات)' : ' (Orders)'),
                        b + (isRtl ? ' (مرتجعات)' : ' (Returns)')
                      ]),
                      isRtl ? 'إجمالي الطلبات (وحدة)' : 'Total Orders (Unit)',
                      isRtl ? 'إجمالي المرتجعات (وحدة)' : 'Total Returns (Unit)',
                    ];
                    const rows = [
                      ...filteredData.map(row => [
                        row.product,
                        ...allBranches.flatMap(b => [
                          row.branchQuantities[b]?.orders || 0,
                          row.branchQuantities[b]?.returns || 0,
                        ]),
                        `${row.totalOrders} ${row.unit}`,
                        `${row.totalReturns} ${row.unit}`,
                      ]),
                      [
                        isRtl ? 'الإجمالي' : 'Total',
                        ...allBranches.flatMap(b => [
                          filteredData.reduce((s, r) => s + (r.branchQuantities[b]?.orders || 0), 0),
                          filteredData.reduce((s, r) => s + (r.branchQuantities[b]?.returns || 0), 0),
                        ]),
                        `${grandTotalOrders} ${filteredData.length ? filteredData[0].unit : ''}`,
                        `${grandTotalReturns} ${filteredData.length ? filteredData[0].unit : ''}`,
                      ],
                    ];
                    await exportToExcel(rows, headers, periodLabel, departmentsLabel, 'مقارنة_الطلبات_المرتجعات', isRtl);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                  {isRtl ? 'Excel (.xlsx)' : 'Excel (.xlsx)'}
                </button>
                <button
                  onClick={() => {
                    const headers = [
                      isRtl ? 'اسم المنتج' : 'Product Name',
                      ...allBranches.flatMap(b => [
                        b + (isRtl ? ' (طلبات)' : ' (Orders)'),
                        b + (isRtl ? ' (مرتجعات)' : ' (Returns)')
                      ]),
                      isRtl ? 'إجمالي الطلبات (وحدة)' : 'Total Orders (Unit)',
                      isRtl ? 'إجمالي المرتجعات (وحدة)' : 'Total Returns (Unit)',
                    ];
                    const rows = [
                      ...filteredData.map(row => [
                        row.product,
                        ...allBranches.flatMap(b => [
                          formatNumber(row.branchQuantities[b]?.orders || 0, isRtl),
                          formatNumber(row.branchQuantities[b]?.returns || 0, isRtl),
                        ]),
                        `${formatNumber(row.totalOrders, isRtl)} ${row.unit}`,
                        `${formatNumber(row.totalReturns, isRtl)} ${row.unit}`,
                      ]),
                      [
                        isRtl ? 'الإجمالي' : 'Total',
                        ...allBranches.flatMap(b => [
                          formatNumber(filteredData.reduce((s, r) => s + (r.branchQuantities[b]?.orders || 0), 0), isRtl),
                          formatNumber(filteredData.reduce((s, r) => s + (r.branchQuantities[b]?.returns || 0), 0), isRtl),
                        ]),
                        `${formatNumber(grandTotalOrders, isRtl)} ${filteredData.length ? filteredData[0].unit : ''}`,
                        `${formatNumber(grandTotalReturns, isRtl)} ${filteredData.length ? filteredData[0].unit : ''}`,
                      ],
                    ];
                    exportToPDF(rows, periodLabel, departmentsLabel, headers, isRtl, 'مقارنة_الطلبات_المرتجعات');
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700"
                >
                  <FileText className="w-4 h-4 text-amber-600" />
                  {isRtl ? 'PDF' : 'PDF'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-3 items-center w-full">
          <div className="w-full lg:w-1/2">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              {isRtl ? 'بحث المنتج' : 'Search Product'}
            </label>
            <ProductSearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={isRtl ? 'ابحث عن منتج...' : 'Search product...'}
              ariaLabel={isRtl ? 'بحث المنتج' : 'Product search'}
            />
          </div>
          <div className="flex flex-col md:flex-row gap-3 w-full lg:w-1/2">
            <div className="w-full md:w-1/2">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {isRtl ? 'الأقسام' : 'Departments'}
              </label>
              <MultiSelectDropdown
                value={selectedDepartments}
                onChange={setSelectedDepartments}
                options={departmentOptions}
                ariaLabel={isRtl ? 'اختر الأقسام' : 'Select departments'}
              />
            </div>
            <div className="w-full md:w-1/2">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {isRtl ? 'الفترة' : 'Period'}
              </label>
              <PeriodDropdown
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                ariaLabel={isRtl ? 'اختر الفترة' : 'Select period'}
              />
            </div>
          </div>
        </div>
        {period === 'custom' && (
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {isRtl ? 'من تاريخ' : 'From Date'}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                className="w-full py-2 px-3 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                dir="ltr"
              />
            </div>
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {isRtl ? 'إلى تاريخ' : 'To Date'}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                className="w-full py-2 px-3 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                dir="ltr"
              />
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-600"></div>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="inline-block p-3 bg-gray-100 rounded-full mb-3">
            <Filter className="w-6 h-6 text-gray-500" />
          </div>
          <p className="text-gray-600 text-sm">{isRtl ? 'لا توجد بيانات مطابقة' : 'No matching data found'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full w-full text-xs border-collapse">
              <thead>
                <tr className="bg-amber-50">
                  <th rowSpan={2} className="px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap min-w-[140px]">
                    {isRtl ? 'اسم المنتج' : 'Product Name'}
                  </th>
                  {allBranches.map((branch) => (
                    <th key={branch} colSpan={2} className="px-2 py-2 font-semibold text-gray-700 text-center border-x min-w-[80px]">
                      {branch}
                    </th>
                  ))}
                  <th rowSpan={2} className="px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap">
                    {isRtl ? 'إجمالي الطلبات (وحدة)' : 'Total Orders (Unit)'}
                  </th>
                  <th rowSpan={2} className="px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap">
                    {isRtl ? 'إجمالي المرتجعات (وحدة)' : 'Total Returns (Unit)'}
                  </th>
                </tr>
                <tr className="bg-amber-50">
                  {allBranches.flatMap((branch) => [
                    <th key={`${branch}-orders`} className="px-2 py-1.5 bg-green-50 text-green-800 font-medium text-[11px]">
                      {isRtl ? 'طلبات' : 'Orders'}
                    </th>,
                    <th key={`${branch}-returns`} className="px-2 py-1.5 bg-red-50 text-red-800 font-medium text-[11px]">
                      {isRtl ? 'مرتجعات' : 'Returns'}
                    </th>,
                  ])}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredData.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={`hover:bg-amber-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    <td className="px-3 py-2.5 text-gray-800 text-center max-w-[140px] truncate" title={row.product}>
                      {row.product}
                    </td>
                    {allBranches.flatMap((branch) => {
                      const ordersQty = row.branchQuantities[branch]?.orders || 0;
                      const returnsQty = row.branchQuantities[branch]?.returns || 0;
                      return [
                        <td key={`${branch}-orders-cell`} className={`px-2 py-2.5 text-center ${ordersQty > 0 ? 'bg-green-100 text-green-800 font-medium' : ''}`}>
                          {formatNumber(ordersQty, isRtl)}
                        </td>,
                        <td key={`${branch}-returns-cell`} className={`px-2 py-2.5 text-center ${returnsQty > 0 ? 'bg-red-100 text-red-800 font-medium' : ''}`}>
                          {formatNumber(returnsQty, isRtl)}
                        </td>,
                      ];
                    })}
                    <td className="px-3 py-2.5 text-green-800 text-center font-medium">
                      {formatNumber(row.totalOrders, isRtl)} {row.unit}
                    </td>
                    <td className="px-3 py-2.5 text-red-800 text-center font-medium">
                      {formatNumber(row.totalReturns, isRtl)} {row.unit}
                    </td>
                  </tr>
                ))}
                <tr className="bg-amber-100 font-bold">
                  <td className="px-3 py-2.5 text-gray-800 text-center">{isRtl ? 'الإجمالي' : 'Total'}</td>
                  {allBranches.flatMap((branch) => [
                    <td key={`${branch}-total-orders`} className="px-2 py-2.5 text-green-800 text-center">
                      {formatNumber(filteredData.reduce((sum, r) => sum + (r.branchQuantities[branch]?.orders || 0), 0), isRtl)}
                    </td>,
                    <td key={`${branch}-total-returns`} className="px-2 py-2.5 text-red-800 text-center">
                      {formatNumber(filteredData.reduce((sum, r) => sum + (r.branchQuantities[branch]?.returns || 0), 0), isRtl)}
                    </td>,
                  ])}
                  <td className="px-3 py-2.5 text-green-800 text-center">
                    {formatNumber(grandTotalOrders, isRtl)} {filteredData.length ? filteredData[0].unit : ''}
                  </td>
                  <td className="px-3 py-2.5 text-red-800 text-center">
                    {formatNumber(grandTotalReturns, isRtl)} {filteredData.length ? filteredData[0].unit : ''}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyOrdersVsReturnsSummary;